import React, { useEffect, useState, useCallback, useRef } from "react";
import { FaCheck } from "react-icons/fa";
import html2canvas from "html2canvas";

import CustomFooter from "../../components/customFooter";
import { Link } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import Switch from "react-switch";
import PickDate from "../../components/common/datePicker";
import moment from "moment";
import GetFunctionalSummaryGraph from "../../hooks/getFunctionalSummaryGraph";
import GetFunctionalChecklist from "../../hooks/getFunctionalChecklist";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { toScoreValue } from "../../utils/scoreState";
import { getScaleValues } from "../../utils/scoringScale";

const used = new Set();

const FilterLozenge = ({ label, count, className = "", isOpen, onClick }) => (
  <button
    type="button"
    className={`functional-filter-lozenge ${
      count !== undefined
        ? "functional-filter-lozenge-has-count"
        : "functional-filter-lozenge-no-count"
    } ${className}`}
    aria-expanded={isOpen}
    onClick={onClick}
  >
    <span className="functional-filter-lozenge-label">{label}</span>
    {count !== undefined && (
      <span className="functional-filter-lozenge-count">{count}</span>
    )}
    <BsChevronDown className="functional-filter-lozenge-caret" size={14} />
  </button>
);

const FilterDropdown = ({
  id,
  label,
  count,
  className,
  panelClassName,
  openDropdown,
  onToggle,
  children,
}) => {
  const isOpen = openDropdown === id;

  return (
    <div className="functional-filter-dropdown">
      <FilterLozenge
        label={label}
        count={count}
        className={className}
        isOpen={isOpen}
        onClick={() => onToggle(id)}
      />
      {isOpen && (
        <div className={`functional-filter-menu ${panelClassName || ""}`}>
          {children}
        </div>
      )}
    </div>
  );
};

// Move CustomLegend outside to prevent re-renders
const CustomLegend = ({
  visibility,
  lineColors,
  searchTerm,
  setSearchTerm,
  toggleVisibility,
  functionalIssues,
}) => {
  // Function to toggle all subheadings for a heading
  const toggleHeadingVisibility = (headingName, subheadings) => {
    const allVisible = subheadings.every(
      (subheading) => visibility[subheading.name]
    );
    subheadings.forEach((subheading) => {
      if (allVisible && visibility[subheading.name]) {
        toggleVisibility(subheading.name);
      } else if (!allVisible && !visibility[subheading.name]) {
        toggleVisibility(subheading.name);
      }
    });
  };

  // Check if all subheadings for a heading are visible
  const areAllSubheadingsVisible = (subheadings) => {
    return (
      subheadings.length > 0 &&
      subheadings.every((subheading) => visibility[subheading.name])
    );
  };
  // Filter legend items based on search term
  const filteredKeys = Object.keys(visibility).filter((key) =>
    key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group subheadings by their headings
  const groupedSubheadings = {};
  if (functionalIssues?.data?.[0]?.headings) {
    functionalIssues.data[0].headings.forEach((heading) => {
      groupedSubheadings[heading.name] = heading.subheadings.filter(
        (subheading) => filteredKeys.includes(subheading.name)
      );
    });
  }

  // Chevron icon
  const ChevronIcon = ({ isOpen, color }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.2s",
        marginRight: 8,
      }}
    >
      <path
        d="M7 10l5 5 5-5"
        stroke={color}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // Track open accordion items for chevron rotation
  const [openItems, setOpenItems] = useState([]);
  const [accordionKey, setAccordionKey] = useState(0);
  const hasInitializedOpenItems = useRef(false);

  useEffect(() => {
    if (
      !hasInitializedOpenItems.current &&
      functionalIssues?.data?.[0]?.headings?.length > 0
    ) {
      setOpenItems([functionalIssues.data[0].headings[0].name]);
      setAccordionKey((prev) => prev + 1);
      hasInitializedOpenItems.current = true;
    }
  }, [functionalIssues]);

  // When search term changes, expand headings that contain matching subheadings
  useEffect(() => {
    if (!functionalIssues?.data?.[0]?.headings) return;

    const searchLower = searchTerm.toLowerCase().trim();

    if (!searchLower) {
      // Restore default: expand first heading only
      setOpenItems([functionalIssues.data[0].headings[0].name]);
      setAccordionKey((prev) => prev + 1);
      return;
    }

    // Find headings that contain at least one matching subheading
    const headingsToExpand = functionalIssues.data[0].headings
      .filter((heading) =>
        heading.subheadings.some((sub) =>
          sub.name.toLowerCase().includes(searchLower)
        )
      )
      .map((heading) => heading.name);

    if (headingsToExpand.length > 0) {
      setOpenItems(headingsToExpand);
      setAccordionKey((prev) => prev + 1);
    }
  }, [searchTerm, functionalIssues]);

  const handleAccordionChange = (uuids) => {
    setOpenItems(uuids);
  };
  return (
    <div className="p-3">
      <h6
        className="font-weight-bold mb-3"
        style={{ fontSize: "14px", color: "#374151" }}
      >
        Functional Outcomes
      </h6>

      {/* Show ALL Switch */}
      <div className="d-flex align-items-center justify-content-around">
        <div
          className="mb-3 d-none align-items-center justify-content-start"
          style={{
            borderRadius: "6px",
            padding: "11px 11px",
            border: `1px solid ${
              Object.values(visibility).every(Boolean) ? "#50A193" : "#374151"
            }`,
          }}
        >
          <span
            className="font-weight-bold"
            style={{
              fontSize: "12px",
              color: Object.values(visibility).every(Boolean)
                ? "#50A193"
                : "#374151",
            }}
          >
            Show All
          </span>
          <Switch
            checked={Object.values(visibility).every(Boolean)}
            onChange={(checked) => {
              Object.keys(visibility).forEach((key) => {
                if (checked && !visibility[key]) {
                  toggleVisibility(key);
                } else if (!checked && visibility[key]) {
                  toggleVisibility(key);
                }
              });
            }}
            onColor="#C7E6F5"
            onHandleColor="#50A193"
            handleDiameter={20}
            uncheckedIcon={false}
            checkedIcon={false}
            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
            height={10}
            width={48}
            className="react-switch"
            id="show-all-switch"
          />
        </div>

        {/* Search Input */}
       
      </div>

      {/* Grouped Legend Items as Accordion */}
      <div
        style={{
          maxHeight: "250px",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <Accordion
          key={accordionKey}
          allowZeroExpanded
          allowMultipleExpanded
          onChange={handleAccordionChange}
          preExpanded={openItems}
        >
          {Object.keys(groupedSubheadings).length > 0 ? (
            Object.entries(groupedSubheadings).map(
              ([headingName, subheadings], idx) => {
                // Pick a color for the heading chevron and dot (use first subheading color if available)
                const headingColor =
                  subheadings.length > 0
                    ? lineColors[subheadings[0].name]
                    : "#6B7280";
                const isOpen = openItems.includes(headingName);
                return (
                  <div key={headingName}>
                    <AccordionItem key={headingName} uuid={headingName}>
                      <AccordionItemHeading className="accordion__primary">
                        <AccordionItemButton
                          className="font-weight-bold mb-2 d-flex align-items-center justify-content-between"
                          style={{
                            fontSize: "13px",
                            color: "#fff",
                            background: "#6090A9",
                            borderRadius: "6px",
                            padding: "8px 12px",
                            marginBottom: 2,
                            cursor: "pointer",
                            border: "none",
                          }}
                        >
                          <div
                            className="d-flex align-items-center"
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            {/* Chevron Icon */}
                            <ChevronIcon
                              className="chevron"
                              isOpen={isOpen}
                              color={"#fff"}
                            />
                            {/* Heading name with truncation and tooltip */}
                            <span
                              style={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "120px",
                              }}
                              title={headingName}
                            >
                              {headingName}
                            </span>
                          </div>
                          {/* Toggle Switch for all subheadings */}
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleHeadingVisibility(headingName, subheadings);
                            }}
                            style={{ marginLeft: "8px" }}
                          >
                            <Switch
                              checked={areAllSubheadingsVisible(subheadings)}
                              onChange={() => {}} // Handled by onClick above
                              onColor="#353C51"
                              onHandleColor="#353C51"
                              handleDiameter={14}
                              uncheckedIcon={false}
                              checkedIcon={false}
                              boxShadow="0px 1px 3px rgba(0, 0, 0, 0.4)"
                              activeBoxShadow="0 0 0 1px #fff"
                              height={8}
                              width={32}
                              className="react-switch"
                            />
                          </div>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel
                        style={{
                          background: "#fff",
                          borderRadius: "0 0 6px 6px",
                        }}
                      >
                        <ul className="list-unstyled p-0 ml-3">
                          {subheadings.map((subheading) => (
                            <li
                              key={subheading.name}
                              className="d-flex align-items-center mb-2"
                            >
                              <div
                                className="mr-2 d-flex align-items-center justify-content-center"
                                style={{
                                  width: "14px",
                                  height: "14px",
                                  backgroundColor: visibility[subheading.name]
                                    ? subheading?.color
                                    : "#D1D5DB",
                                  borderRadius: "3px",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  border: `2px solid ${
                                    visibility[subheading.name]
                                      ? subheading?.color
                                      : "#D1D5DB"
                                  }`,
                                }}
                                onClick={() =>
                                  toggleVisibility(subheading.name)
                                }
                              >
                                {visibility[subheading.name] && (
                                  <FaCheck color="white" size={8} />
                                )}
                              </div>
                              <span
                                className="cursor-pointer"
                                style={{
                                  fontSize: "12px",
                                  color: visibility[subheading.name]
                                    ? "#374151"
                                    : "#9CA3AF",
                                  fontWeight: visibility[subheading.name]
                                    ? "500"
                                    : "400",
                                  transition: "all 0.2s ease",
                                }}
                                onClick={() =>
                                  toggleVisibility(subheading.name)
                                }
                              >
                                {subheading.name}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </AccordionItemPanel>
                    </AccordionItem>
                  </div>
                );
              }
            )
          ) : (
            <div className="text-center py-3">
              <small style={{ color: "#9CA3AF", fontSize: "12px" }}>
                No functional outcomes found
              </small>
            </div>
          )}
        </Accordion>
      </div>
    </div>
  );
};

const FunctionalSummariesGraph = (props) => {
  // TOGGLE THIS FLAG TO ENABLE/DISABLE DEFAULT 7-DAY FILTER
  // Set to true: Shows last 7 days by default
  // Set to false: Shows full data range (original behavior)
  const USE_DEFAULT_7_DAY_FILTER = true;
  const graphFilters = props.location?.state?.graphFilters;
  const parseStateDate = (value) =>
    value && moment(value).isValid() ? moment(value).toDate() : null;

  const [startDate, setStartDate] = useState(
    parseStateDate(graphFilters?.startDate)
  );
  const [endDate, setEndDate] = useState(parseStateDate(graphFilters?.endDate));
  const [reportStartDate, setReportStartDate] = useState(null);
  const [reportEndDate, setReportEndDate] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(
    graphFilters?.filterStartDate || null
  );
  const [filterEndDate, setFilterEndDate] = useState(
    graphFilters?.filterEndDate || null
  );
  const [selectedScore, setSelectedScore] = useState(
    graphFilters?.selectedScore ?? null
  );
  const [filterScores, setFilterScores] = useState(
    graphFilters?.filterScores || null
  );
  const [selectedFunctionalIssues, setSelectedFunctionalIssues] = useState(
    graphFilters?.selectedFunctionalIssues || []
  );
  const [filterFunctionalIssues, setFilterFunctionalIssues] = useState(
    graphFilters?.filterFunctionalIssues || null
  );
  const [filterProviders, setFilterProviders] = useState(
    graphFilters?.filterProviders || null
  );
  const [data, setData] = useState([]);
  const patient = localStorage.getItem("patient");
  const [checked, setChecked] = useState(
    graphFilters?.checked || { check: false, attr: "" }
  );
  const [checkedScores, setCheckedScores] = useState({
    ...(graphFilters?.checkedScores || {
      check: false,
      attr: "",
    }),
  });

  const [visibility, setVisibility] = useState({});
  const [lineColors, setLineColors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [hasSetDefaultFilter, setHasSetDefaultFilter] = useState(false);
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [draftStartDate, setDraftStartDate] = useState(startDate);
  const [draftEndDate, setDraftEndDate] = useState(endDate);
  const [draftFunctionalIssues, setDraftFunctionalIssues] = useState(
    selectedFunctionalIssues
  );
  const [draftScore, setDraftScore] = useState(selectedScore);
  const [expandedFilterHeadingIds, setExpandedFilterHeadingIds] = useState([]);
  const filterBarRef = useRef(null);

  const {
    data: unFormattedData,
    isLoading,
    error,
  } = GetFunctionalSummaryGraph(
    patient,
    filterStartDate,
    filterEndDate,
    filterScores,
    filterProviders,
    filterFunctionalIssues
  );

  const { data: functionalIssues, isLoading: checklistLoading } =
    GetFunctionalChecklist();

  useEffect(() => {
    moment.updateLocale("en", {
      monthsShort: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sept",
        "Oct",
        "Nov",
        "Dec",
      ],
    });

    // Apply default 7-day filter on component mount (if enabled)
    if (
      USE_DEFAULT_7_DAY_FILTER &&
      !hasSetDefaultFilter &&
      !graphFilters
    ) {
      const today = moment();
      const sevenDaysAgo = moment().subtract(7, "days");

      setFilterStartDate(formatDateForAPI(sevenDaysAgo.toDate()));
      setFilterEndDate(formatDateForAPI(today.toDate()));
      setHasSetDefaultFilter(true);
    } else if (!hasSetDefaultFilter) {
      setHasSetDefaultFilter(true);
    }
  }, [
    USE_DEFAULT_7_DAY_FILTER,
    hasSetDefaultFilter,
    graphFilters,
  ]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        openFilterDropdown &&
        filterBarRef.current &&
        !filterBarRef.current.contains(event.target)
      ) {
        setOpenFilterDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [openFilterDropdown]);

  useEffect(() => {
    if (openFilterDropdown === "time") {
      setDraftStartDate(
        filterStartDate && moment(filterStartDate).isValid()
          ? moment(filterStartDate).toDate()
          : startDate
      );
      setDraftEndDate(
        filterEndDate && moment(filterEndDate).isValid()
          ? moment(filterEndDate).toDate()
          : endDate
      );
    }

    if (openFilterDropdown === "issues") {
      setDraftFunctionalIssues(filterFunctionalIssues || []);
      const firstHeadingId = functionalIssues?.data?.[0]?.headings?.[0]?.id;
      setExpandedFilterHeadingIds(firstHeadingId ? [firstHeadingId] : []);
    }

    if (openFilterDropdown === "score") {
      setDraftScore(
        selectedScore ||
          (filterScores?.length > 0 ? Math.max(...filterScores) : null)
      );
    }
  }, [
    openFilterDropdown,
    filterStartDate,
    filterEndDate,
    startDate,
    endDate,
    filterFunctionalIssues,
    functionalIssues,
    selectedScore,
    filterScores,
  ]);

  useEffect(() => {
    // Group scores by week (starting Sunday) and subheading, then calculate weekly median
    const weekData = {};
    const initialVisiblity = {};
    const lineColors = {};
    let minDate = null;
    let maxDate = null;

    // Build a map of subheadingName -> color from functionalIssues, and one of
    // subheadingName -> scoring scale: the payload is a flat list of scores
    // with no heading on it, so the range each point is valid on has to be
    // looked up by name.
    const subheadingColorMap = {};
    const subheadingScaleMap = {};
    const firstHeadingSubheadings = new Set();
    if (functionalIssues?.data?.[0]?.headings) {
      functionalIssues.data[0].headings.forEach((heading, index) => {
        heading.subheadings.forEach((subheading) => {
          subheadingColorMap[subheading.name] = subheading.color || "#3B82F6"; // fallback color
          subheadingScaleMap[subheading.name] = heading.scoringScale;
          if (index === 0) {
            firstHeadingSubheadings.add(subheading.name);
          }
        });
      });
    }

    if (unFormattedData?.data && unFormattedData?.data.length > 0) {
      unFormattedData.data.forEach(
        ({ assessmentDate, subheadingName, score, notApplicable }) => {
          // N/A and unanswered items carry no value, so they stay out of the
          // weekly medians entirely.
          const scoreValue = notApplicable
            ? null
            : toScoreValue(score, subheadingScaleMap[subheadingName]);
          if (scoreValue === null) return;
          // Find the week start (Sunday) for this date
          const m = moment.utc(assessmentDate);
          const weekStart = m.clone().startOf("week"); // Sunday as start
          const weekStartStr = weekStart.format("YYYY-MM-DD");
          if (!weekData[weekStartStr]) weekData[weekStartStr] = {};
          if (!weekData[weekStartStr][subheadingName])
            weekData[weekStartStr][subheadingName] = [];
          weekData[weekStartStr][subheadingName].push(scoreValue);
          if (!lineColors[subheadingName])
            lineColors[subheadingName] =
              subheadingColorMap[subheadingName] || "#3B82F6";
          initialVisiblity[subheadingName] =
            firstHeadingSubheadings.has(subheadingName);
          if (!minDate || m.isBefore(minDate)) minDate = m.clone();
          if (!maxDate || m.isAfter(maxDate)) maxDate = m.clone();
        }
      );

      // Generate all week start dates (Sundays) from minDate to the week containing maxDate, always including the last week
      const allWeeks = [];
      if (minDate && maxDate) {
        let current = minDate.clone().startOf("week");
        // Always include the week containing the last assessment date, even if it's after today
        let last = maxDate.clone().endOf("week").startOf("week");
        // If the last week is not today or in the future, add one more week to always show the last week
        const today = moment.utc().startOf("week");
        if (last.isBefore(maxDate.clone())) {
          last = maxDate.clone().startOf("week");
        }
        while (current.isSameOrBefore(last)) {
          allWeeks.push(current.format("YYYY-MM-DD"));
          current.add(1, "week");
        }
        // If the last assessment is not in the last week, add that week too
        const lastAssessmentWeek = maxDate
          .clone()
          .startOf("week")
          .format("YYYY-MM-DD");
        if (!allWeeks.includes(lastAssessmentWeek)) {
          allWeeks.push(lastAssessmentWeek);
        }
        // Sort weeks to ensure order
        allWeeks.sort((a, b) => new Date(a) - new Date(b));
      }

      // Build data array: one entry per week, with median for each subheading
      // If a week is missing a subheading, fill with last valid score (carry forward)
      const lastValid = {};
      const dataArr = allWeeks.map((weekStr) => {
        const entry = { date: moment(weekStr).format("D MMM YYYY") };
        Object.keys(lineColors).forEach((subheading) => {
          if (weekData[weekStr] && weekData[weekStr][subheading]) {
            // Calculate median
            const scores = weekData[weekStr][subheading]
              .slice()
              .sort((a, b) => a - b);
            const mid = Math.floor(scores.length / 2);
            let median;
            if (scores.length % 2 === 0) {
              median = Math.floor((scores[mid - 1] + scores[mid]) / 2);
            } else {
              median = Math.floor(scores[mid]);
            }
            entry[subheading] = median;
            lastValid[subheading] = median;
          } else {
            // Carry forward last valid score if exists
            entry[subheading] =
              lastValid[subheading] !== undefined
                ? lastValid[subheading]
                : null;
          }
        });
        return entry;
      });

      setReportStartDate(minDate);
      setReportEndDate(maxDate);
      setLineColors(lineColors);
      setVisibility(initialVisiblity);
      setData(dataArr);
    }
  }, [unFormattedData, functionalIssues]);

  // Sync searchTerm → visibility so the graph updates when the user searches
  useEffect(() => {
    if (Object.keys(lineColors).length === 0) return;

    const searchLower = searchTerm.toLowerCase().trim();

    if (!searchLower) {
      // Restore default: only first-heading subheadings visible
      const firstHeadingSubheadings = new Set();
      if (functionalIssues?.data?.[0]?.headings?.[0]) {
        functionalIssues.data[0].headings[0].subheadings.forEach((sub) => {
          firstHeadingSubheadings.add(sub.name);
        });
      }
      const restoredVisibility = {};
      Object.keys(lineColors).forEach((key) => {
        restoredVisibility[key] = firstHeadingSubheadings.has(key);
      });
      setVisibility(restoredVisibility);
    } else {
      // Show only subheadings whose name matches the search term
      const newVisibility = {};
      Object.keys(lineColors).forEach((key) => {
        newVisibility[key] = key.toLowerCase().includes(searchLower);
      });
      setVisibility(newVisibility);
    }
  }, [searchTerm, lineColors, functionalIssues]);

  const handleChecked = (status, e, id) => {
    switch (id) {
      case "function":
        setChecked({ check: status, attr: id });
        // If functional issues switch is turned off, clear selected functional issues
        if (!status) {
          setSelectedFunctionalIssues([]);
        }
        break;
      case "score":
        setCheckedScores({ check: status, attr: id });
        // If score switch is turned off, clear selected score
        if (!status) {
          setSelectedScore(null);
        }
        break;
      default:
        break;
    }
  };

  const toggleVisibility = (key) => {
    setVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Date filter functions
  const handleTodayFilter = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    setStartDate(today);
    setEndDate(tomorrow);
  };

  const handleLast7DaysFilter = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    setStartDate(sevenDaysAgo);
    setEndDate(today);
  };

  const handleLast30DaysFilter = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    setStartDate(thirtyDaysAgo);
    setEndDate(today);
  };

  // Format date for API (YYYY-MM-DDTHH:mm:ss.SSS)
  const formatDateForAPI = (date) => {
    if (!date) return null;
    return moment(date).format("YYYY-MM-DDTHH:mm:ss.SSS");
  };

  const normalizePickerDate = (date) => {
    if (!date) return null;
    const normalized = new Date(date);
    const offset = normalized.getTimezoneOffset();
    normalized.setMinutes(normalized.getMinutes() - offset);
    return normalized;
  };

  const toggleFilterDropdown = (id) => {
    setOpenFilterDropdown(openFilterDropdown === id ? null : id);
  };

  const selectDraftPreset = (days) => {
    const end = moment().startOf("day");
    const start = moment(end).subtract(days - 1, "days");
    setDraftStartDate(start.toDate());
    setDraftEndDate(end.toDate());
  };

  // "≤ n" means every value the checklist's scales offer up to n, not 1..n:
  // an ICF heading is scored from 0, so counting up from 1 would drop the
  // lowest score on the scale out of the filter.
  const buildScoreRange = (score) => {
    if (score === null || score === undefined || score === "") return null;
    const limit = Number(score);
    return graphScaleValues.filter((value) => value <= limit);
  };

  const handleApplyTimeDropdown = () => {
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    setFilterStartDate(formatDateForAPI(draftStartDate));
    setFilterEndDate(formatDateForAPI(draftEndDate));
    setOpenFilterDropdown(null);
  };

  const handleClearTimeDropdown = () => {
    setDraftStartDate(null);
    setDraftEndDate(null);
    setStartDate(null);
    setEndDate(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setOpenFilterDropdown(null);
  };

  const toggleDraftFunctionalIssue = (issueId) => {
    setDraftFunctionalIssues((currentIssues) =>
      currentIssues.includes(issueId)
        ? currentIssues.filter((id) => id !== issueId)
        : [...currentIssues, issueId]
    );
  };

  const toggleExpandedFilterHeading = (headingId) => {
    setExpandedFilterHeadingIds((currentHeadings) =>
      currentHeadings.includes(headingId)
        ? currentHeadings.filter((id) => id !== headingId)
        : [...currentHeadings, headingId]
    );
  };

  const handleApplyFunctionalIssuesDropdown = () => {
    setSelectedFunctionalIssues(draftFunctionalIssues);
    setFilterFunctionalIssues(
      draftFunctionalIssues.length > 0 ? draftFunctionalIssues : null
    );
    setChecked({
      check: draftFunctionalIssues.length > 0,
      attr: draftFunctionalIssues.length > 0 ? "function" : "",
    });
    setOpenFilterDropdown(null);
  };

  const handleClearFunctionalIssuesDropdown = () => {
    setDraftFunctionalIssues([]);
    setSelectedFunctionalIssues([]);
    setFilterFunctionalIssues(null);
    setChecked({ check: false, attr: "" });
    setOpenFilterDropdown(null);
  };

  const handleApplyScoreDropdown = () => {
    // 0 is a real score on an ICF heading, so "nothing picked" is null rather
    // than falsy.
    const hasPick =
      draftScore !== null && draftScore !== undefined && draftScore !== "";
    const nextScore = hasPick ? Number(draftScore) : null;
    setSelectedScore(nextScore);
    setFilterScores(hasPick ? buildScoreRange(nextScore) : null);
    setCheckedScores({
      check: hasPick,
      attr: hasPick ? "score" : "",
    });
    setOpenFilterDropdown(null);
  };

  const handleClearScoreDropdown = () => {
    setDraftScore(null);
    setSelectedScore(null);
    setFilterScores(null);
    setCheckedScores({ check: false, attr: "" });
    setOpenFilterDropdown(null);
  };

  // Handle score selection (radio button logic)
  const handleScoreSelection = (score) => {
    // If the same score is clicked again, deselect it
    if (selectedScore === score) {
      setSelectedScore(null);
    } else {
      setSelectedScore(score);
    }
  };

  // Handle search button click
  const handleSearch = () => {
    const formattedStartDate = formatDateForAPI(startDate);
    const formattedEndDate = formatDateForAPI(endDate);

    setFilterStartDate(formattedStartDate);
    setFilterEndDate(formattedEndDate);

    // Set score filter based on switch state and selected score
    if (checkedScores.check && selectedScore !== null) {
      // Transform selected score to include all scores within range
      setFilterScores(buildScoreRange(selectedScore));
    } else {
      setFilterScores(null);
    }

    // Set functional issues filter based on switch state and selected functional issues
    if (checked.check && selectedFunctionalIssues.length > 0) {
      setFilterFunctionalIssues(selectedFunctionalIssues);
    } else {
      setFilterFunctionalIssues(null);
    }

    // Close the modal
    window.$("#filterModal").modal("hide");
  };

  // Download: show preview popup, capture it, then close
  const [showDownloadPreview, setShowDownloadPreview] = useState(false);
  const downloadRef = useRef(null);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = () => {
    setShowDownloadPreview(true);
  };

  const handleConfirmDownload = async () => {
    if (!downloadRef.current) return;
    setIsDownloading(true);
    try {
      const canvas = await html2canvas(downloadRef.current, {
        backgroundColor: "#FFFFFF",
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `functional-scores-graph-${moment().format("YYYY-MM-DD")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Failed to download graph:", err);
    }
    setIsDownloading(false);
    setShowDownloadPreview(false);
  };

  const handleCancelDownload = () => {
    setShowDownloadPreview(false);
  };

  // Clear filters
  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setSelectedScore(null);
    setFilterScores(null);
    setSelectedFunctionalIssues([]);
    setFilterFunctionalIssues(null);
    setFilterProviders(null);
    setDraftStartDate(null);
    setDraftEndDate(null);
    setDraftScore(null);
    setDraftFunctionalIssues([]);
    setChecked({ check: false, attr: "" });
    setCheckedScores({ check: false, attr: "" });
  };

  const formatFilterDateRangeDisplay = () => {
    if (
      filterStartDate &&
      filterEndDate &&
      moment(filterStartDate).isValid() &&
      moment(filterEndDate).isValid()
    ) {
      const startMoment = moment(filterStartDate);
      const endMoment = moment(filterEndDate);

      if (startMoment.isSame(endMoment, "day")) {
        return startMoment.format("D MMM YYYY");
      }

      return `${startMoment.format("D MMM YYYY")} - ${endMoment.format(
        "D MMM YYYY"
      )}`;
    }

    if (
      reportStartDate &&
      reportEndDate &&
      moment(reportStartDate).isValid() &&
      moment(reportEndDate).isValid()
    ) {
      const startMoment = moment(reportStartDate);
      const endMoment = moment(reportEndDate);

      if (startMoment.isSame(endMoment, "day")) {
        return startMoment.format("D MMM YYYY");
      }

      return `${startMoment.format("D MMM YYYY")} - ${endMoment.format(
        "D MMM YYYY"
      )}`;
    }

    return "No data available";
  };

  const functionalIssueFilterCount = filterFunctionalIssues?.length || 0;
  const scoreFilterLabel =
    filterScores?.length > 0
      ? `Score ${
          filterScores.length === 1
            ? filterScores[0]
            : `<= ${Math.max(...filterScores)}`
        }`
      : "Score";
  const functionalFilterHeadings =
    functionalIssues?.data?.[0]?.headings || [];

  // The graph plots every heading on one axis, so the scores it can show are
  // the union of the scales in play — just 1-5 when they are all
  // POMMe-Health.
  const graphScaleValues = (() => {
    const values = Array.from(
      new Set(
        functionalFilterHeadings.flatMap((heading) =>
          getScaleValues(heading.scoringScale),
        ),
      ),
    ).sort((a, b) => a - b);
    return values.length ? values : getScaleValues();
  })();
  const scoreOptions = graphScaleValues.map((value, index) => ({
    // The lowest value stands for itself; every higher one filters to "this or
    // anything below it".
    label: index === 0 ? String(value) : `≤${value}`,
    value,
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="bg-white p-3 shadow-lg rounded-lg border"
          style={{ minWidth: "200px" }}
        >
          <p
            className="font-weight-bold mb-2"
            style={{ fontSize: "14px" }}
          >{`Date: ${label}`}</p>
          {payload.map((entry, index) => (
            <p
              key={index}
              className="mb-1"
              style={{ color: entry.color, fontSize: "13px" }}
            >
              <span className="font-weight-semibold">{entry.dataKey}:</span>{" "}
              {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100">
        <div className="card border-0 h-100">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="font-weight-lighter">
                HCP: VIEW Functional Summaries
              </h5>
              <div className="d-flex align-items-center">
                <Link
                  className="btn btn-primary p-2 rounded"
                  to={{
                    pathname: "/hcp/patient/summary_functions",
                    state: {
                      graphFilters: {
                        startDate: startDate
                          ? moment(startDate).toISOString()
                          : null,
                        endDate: endDate ? moment(endDate).toISOString() : null,
                        filterStartDate,
                        filterEndDate,
                        selectedScore,
                        filterScores,
                        selectedProviders: graphFilters?.selectedProviders || [],
                        checkedNames:
                          graphFilters?.checkedNames || {
                            check: false,
                            attr: "",
                          },
                        filterProviders,
                        selectedFunctionalIssues,
                        filterFunctionalIssues,
                        checked,
                        checkedScores,
                      },
                    },
                  }}
                >
                  <small className="font-weight-bold">Go to Table</small>
                </Link>
              </div>
            </div>
            <hr />
            <div className="d-flex justify-content-between align-items-start pb-4">
              <div className="flex-grow-1 pr-3">
                <div className="functional-filter-bar" ref={filterBarRef}>
                  <FilterDropdown
                    id="time"
                    label="Custom Time Period"
                    className="functional-filter-lozenge-time"
                    panelClassName="functional-filter-menu-time"
                    openDropdown={openFilterDropdown}
                    onToggle={toggleFilterDropdown}
                  >
                    <div className="functional-filter-menu-content">
                      <div className="functional-filter-presets">
                        <button
                          type="button"
                          className="functional-filter-preset"
                          onClick={() => selectDraftPreset(1)}
                        >
                          Today
                        </button>
                        <button
                          type="button"
                          className="functional-filter-preset"
                          onClick={() => selectDraftPreset(7)}
                        >
                          Last 7 Days
                        </button>
                        <button
                          type="button"
                          className="functional-filter-preset"
                          onClick={() => selectDraftPreset(30)}
                        >
                          Last 30 Days
                        </button>
                      </div>

                      <label className="functional-filter-menu-label">
                        Period
                      </label>
                      <button
                        type="button"
                        className="functional-filter-select"
                        disabled
                      >
                        Custom Date
                        <BsChevronDown size={14} />
                      </button>

                      <div className="functional-filter-section-title">
                        Custom Date
                      </div>
                      <hr className="functional-filter-divider" />

                      <div className="functional-filter-date-grid">
                        <div>
                          <small className="functional-filter-field-label">
                            Start Date
                          </small>
                          <PickDate
                            date={draftStartDate}
                            setDate={(date) =>
                              setDraftStartDate(normalizePickerDate(date))
                            }
                            required={false}
                          />
                        </div>
                        <div>
                          <small className="functional-filter-field-label">
                            End Date
                          </small>
                          <PickDate
                            date={draftEndDate}
                            setDate={(date) =>
                              setDraftEndDate(normalizePickerDate(date))
                            }
                            required={false}
                          />
                        </div>
                      </div>

                      <div className="functional-filter-actions">
                        <button
                          type="button"
                          className="functional-filter-action functional-filter-action-clear"
                          onClick={handleClearTimeDropdown}
                        >
                          CLEAR
                        </button>
                        <button
                          type="button"
                          className="functional-filter-action functional-filter-action-apply"
                          onClick={handleApplyTimeDropdown}
                        >
                          APPLY
                        </button>
                      </div>
                    </div>
                  </FilterDropdown>
                  <span className="functional-filter-bar-label">
                    Custom Filters:
                  </span>
                  <FilterDropdown
                    id="issues"
                    label="Functional Issues"
                    count={functionalIssueFilterCount}
                    className="functional-filter-lozenge-issues"
                    panelClassName="functional-filter-menu-issues"
                    openDropdown={openFilterDropdown}
                    onToggle={toggleFilterDropdown}
                  >
                    <div className="functional-filter-menu-content h-100">
                      <p className="functional-filter-menu-prompt">
                        Choose as many as applicable:
                      </p>

                      <div className="functional-filter-scroll">
                        {checklistLoading && (
                          <small className="text-muted">
                            Loading functional issues...
                          </small>
                        )}
                        {!checklistLoading &&
                          functionalFilterHeadings.map((heading) => {
                            const isExpanded =
                              expandedFilterHeadingIds.includes(heading.id);

                            return (
                              <div
                                key={heading.id}
                                className="functional-filter-heading"
                              >
                                <button
                                  type="button"
                                  className="functional-filter-heading-button"
                                  onClick={() =>
                                    toggleExpandedFilterHeading(heading.id)
                                  }
                                >
                                  {isExpanded ? (
                                    <BsChevronUp size={18} />
                                  ) : (
                                    <BsChevronDown size={18} />
                                  )}
                                  <span>{heading.name}</span>
                                </button>

                                {isExpanded && (
                                  <div className="functional-filter-options">
                                    {heading.subheadings.map((item) => (
                                      <div
                                        key={item.id}
                                        className="functional-filter-check-row dropdown-item noFilterHover form-group form-check wa mb-2"
                                      >
                                        <input
                                          type="checkbox"
                                          className="form-check-input mt-2"
                                          id={`graphFunctionalIssueCheck${item.id}`}
                                          checked={draftFunctionalIssues.includes(
                                            item.id
                                          )}
                                          onChange={() =>
                                            toggleDraftFunctionalIssue(item.id)
                                          }
                                        />
                                        <label
                                          className="form-check-label f-13 ml-2 mb-0"
                                          htmlFor={`graphFunctionalIssueCheck${item.id}`}
                                        >
                                          <small>{item.name}</small>
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        {!checklistLoading &&
                          functionalFilterHeadings.length === 0 && (
                            <small className="text-muted">
                              No functional issues available
                            </small>
                          )}
                      </div>

                      <div className="functional-filter-actions">
                        <button
                          type="button"
                          className="functional-filter-action functional-filter-action-clear"
                          onClick={handleClearFunctionalIssuesDropdown}
                        >
                          CLEAR
                        </button>
                        <button
                          type="button"
                          className="functional-filter-action functional-filter-action-apply"
                          onClick={handleApplyFunctionalIssuesDropdown}
                        >
                          APPLY
                        </button>
                      </div>
                    </div>
                  </FilterDropdown>
                  <FilterDropdown
                    id="score"
                    label={scoreFilterLabel}
                    className="functional-filter-lozenge-score"
                    panelClassName="functional-filter-menu-score"
                    openDropdown={openFilterDropdown}
                    onToggle={toggleFilterDropdown}
                  >
                    <div className="functional-filter-menu-content">
                      <p className="functional-filter-menu-prompt">
                        Choose one score:
                      </p>
                      <div className="functional-filter-score-options">
                        {scoreOptions.map((score) => (
                          <div
                            key={score.value}
                            className="functional-filter-radio-row dropdown-item noFilterHover form-group form-check wa mb-1"
                          >
                            <input
                              type="radio"
                              className="form-check-input mt-2"
                              id={`graphScoreRadio${score.value}`}
                              name="graphFunctionalScoreFilter"
                              checked={draftScore === score.value}
                              onChange={() => setDraftScore(score.value)}
                            />
                            <label
                              className="form-check-label f-13 ml-2 mb-0"
                              htmlFor={`graphScoreRadio${score.value}`}
                            >
                              <small>{score.label}</small>
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="functional-filter-actions">
                        <button
                          type="button"
                          className="functional-filter-action functional-filter-action-clear"
                          onClick={handleClearScoreDropdown}
                        >
                          CLEAR
                        </button>
                        <button
                          type="button"
                          className="functional-filter-action functional-filter-action-apply"
                          onClick={handleApplyScoreDropdown}
                        >
                          APPLY
                        </button>
                      </div>
                    </div>
                  </FilterDropdown>
                </div>
                <p className="functional-filter-date-summary mb-0">
                  {formatFilterDateRangeDisplay()}
                </p>
              </div>
              <div className="d-flex align-items-center">
                 <div className="mr-3">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search subheadings..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      fontSize: "12px",
                      border: "1px solid #D1D5DB",
                      borderRadius: "6px",
                      backgroundColor: "#F9FAFB",
                    }}
                  />
                </div>
              </div>
            </div>
            <div
              className="row shadow-lg rounded-lg border-0 mx-1"
              style={{
                backgroundColor: "#FAFAFA",
                border: "1px solid #E5E7EB",
              }}
            >
              <div className="col-md-8 p-4" style={{ height: "400px" }}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5
                    className="font-weight-bold mb-0"
                    style={{ color: "#1F2937", fontSize: "18px" }}
                  >
                    Functional Scores Graph
                  </h5>
                  <div className="d-flex align-items-center">
                    <span style={{ fontSize: "12px", color: "#6B7280" }}>
                      Score Range: 1-5
                    </span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height="90%">
                  <LineChart
                    data={data}
                    margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
                  >
                    <defs>
                      {Object.keys(visibility).map((key) => (
                        <linearGradient
                          key={key}
                          id={`gradient-${key}`}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={lineColors[key]}
                            stopOpacity={0.1}
                          />
                          <stop
                            offset="95%"
                            stopColor={lineColors[key]}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid
                      stroke="#E5E7EB"
                      strokeDasharray="3 3"
                      vertical={false}
                      horizontal={true}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: "#6B7280" }}
                      axisLine={{ stroke: "#E5E7EB" }}
                      tickLine={{ stroke: "#E5E7EB" }}
                      tickMargin={10}
                      label={{
                        value: "Beginning Week of (Sunday)",
                        position: "insideBottom",
                        offset: -15,
                        style: {
                          textAnchor: "middle",
                          fontSize: "13px",
                          fill: "#6B7280",
                          fontWeight: 500,
                        },
                      }}
                    />
                    <YAxis
                      domain={[
                        graphScaleValues[0],
                        graphScaleValues[graphScaleValues.length - 1],
                      ]}
                      ticks={graphScaleValues}
                      tick={{ fontSize: 12, fill: "#6B7280" }}
                      axisLine={{ stroke: "#E5E7EB" }}
                      tickLine={{ stroke: "#E5E7EB" }}
                      tickMargin={10}
                      label={{
                        value: "Median Scores",
                        angle: -90,
                        position: "insideLeft",
                        style: {
                          textAnchor: "middle",
                          fontSize: "12px",
                          fill: "#6B7280",
                          margin: "12px",
                        },
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {Object.keys(visibility).map(
                      (key) =>
                        visibility[key] && (
                          <Line
                            key={key}
                            dataKey={key}
                            stroke={lineColors[key]}
                            strokeWidth={3}
                            fill="none"
                            dot={{
                              r: 4,
                              fill: lineColors[key],
                              strokeWidth: 2,
                              stroke: "#ffffff",
                            }}
                            activeDot={{
                              r: 6,
                              fill: lineColors[key],
                              strokeWidth: 3,
                              stroke: "#ffffff",
                              style: { cursor: "pointer" },
                            }}
                            connectNulls={true}
                            type="number"
                            animationDuration={1000}
                          />
                        )
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div
                className="col-md-4"
                style={{
                  backgroundColor: "#F9FAFB",
                  borderLeft: "1px solid #E5E7EB",
                }}
              >
                <CustomLegend
                  visibility={visibility}
                  lineColors={lineColors}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  toggleVisibility={toggleVisibility}
                  functionalIssues={functionalIssues}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <CustomFooter /> */}

      <CustomFooter showSignButton={false} align="right" onDownload={handleDownload} />

      {/* Filter Modal */}
      <div
        className="modal fade"
        id="filterModal"
        tabIndex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content pt-3">
            <div className="modal-header border-0">
              <div className="flex-grow-1 text-center">
                <h5 className="text_primary" style={{ fontWeight: 600 }}>
                  Search Filter
                </h5>
              </div>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 40, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div className="modal-body mb-3 p-0">
              <div className="d-flex justify-content-between align-items-center mb-4 px-3">
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleTodayFilter}
                >
                  <small>Today</small>
                </button>
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleLast7DaysFilter}
                >
                  <small>Last 7 days</small>
                </button>
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleLast30DaysFilter}
                >
                  <small>Last 30 days</small>
                </button>
              </div>
              <small className="text-muted ml-3">Custom Date</small>
              <hr className="my-2 mx-3" />
              <div className="row mb-3 mx-1">
                <div className="col-md-6">
                  <small className="text-muted">Start Date</small>
                  <PickDate
                    date={startDate}
                    setDate={setStartDate}
                    required={false}
                  />
                </div>
                <div className="col-md-6">
                  <small className="text-muted">End Date</small>
                  <PickDate
                    date={endDate}
                    setDate={setEndDate}
                    required={false}
                  />
                </div>
              </div>

              <Accordion allowZeroExpanded allowMultipleExpanded>
                <div className="chevron" />
                <AccordionItem uuid="functional-issues-main">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Functional Issues</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={checked.attr === "function" && checked.check}
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="function"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 px-4">
                    <Accordion allowZeroExpanded allowMultipleExpanded>
                      {checklistLoading && (
                        <div className="ml-4 py-2">
                          <small className="text-muted">
                            Loading functional issues...
                          </small>
                        </div>
                      )}
                      {functionalIssues &&
                        functionalIssues?.data[0]?.headings?.map(
                          (heading, headingIndex) => (
                            <AccordionItem
                              key={heading.id}
                              uuid={`functional-heading-${heading.id}`}
                            >
                              <AccordionItemHeading className="accordion_transparent">
                                <AccordionItemButton>
                                  <small className="text-muted">
                                    {heading.name}
                                  </small>
                                </AccordionItemButton>
                              </AccordionItemHeading>
                              <AccordionItemPanel className="ml-3">
                                <small className="text-muted f-13">
                                  Choose as many as applicable:
                                </small>
                                <div className="mt-2">
                                  {heading.subheadings.map((item, i) => (
                                    <div
                                      className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-2"
                                      key={i}
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input mt-2"
                                        id={`functionalIssueCheck${item.id}`}
                                        checked={selectedFunctionalIssues.includes(
                                          item.id
                                        )}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedFunctionalIssues([
                                              ...selectedFunctionalIssues,
                                              item.id,
                                            ]);
                                          } else {
                                            setSelectedFunctionalIssues(
                                              selectedFunctionalIssues.filter(
                                                (id) => id !== item.id
                                              )
                                            );
                                          }
                                        }}
                                        disabled={!checked.check}
                                      />
                                      <label
                                        className="form-check-label f-13 ml-2"
                                        htmlFor={`functionalIssueCheck${item.id}`}
                                      >
                                        <small>{item.name}</small>
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </AccordionItemPanel>
                            </AccordionItem>
                          )
                        )}
                      {!checklistLoading &&
                        (!functionalIssues ||
                          !functionalIssues?.data[0]?.headings ||
                          functionalIssues?.data[0]?.headings.length === 0) && (
                          <div className="ml-4 py-2">
                            <small className="text-muted">
                              No functional issues available
                            </small>
                          </div>
                        )}
                    </Accordion>
                  </AccordionItemPanel>
                </AccordionItem>
                <AccordionItem uuid="scores-filter">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Scores</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={
                        checkedScores.attr === "score" && checkedScores.check
                      }
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="score"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">
                      Choose one option only to select the range of scores you
                      want to focus on:
                    </small>
                    <div className="d-flex align-items-center mt-2">
                      {scoreOptions.map(({ value: item }, i) => (
                        <div
                          className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-1"
                          key={i}
                        >
                          <input
                            type="radio"
                            className="form-check-input mt-2"
                            id={`scoreRadio${item}`}
                            name="scoreRadio"
                            checked={selectedScore === item}
                            onChange={() => handleScoreSelection(item)}
                            disabled={!checkedScores.check}
                          />
                          <label
                            className="form-check-label f-13 ml-2"
                            htmlFor={`scoreRadio${item}`}
                          >
                            <small>
                              {i === 0 ? (
                                String(item)
                              ) : (
                                <>
                                  <span
                                    style={{
                                      fontSize: "16px",
                                    }}
                                  >
                                    ≤
                                  </span>{" "}
                                  {item}
                                </>
                              )}
                            </small>
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="ml-4 mt-3">
                      <small className="text-muted f-13">
                        (The full timeline of the functional outcomes containing
                        these scores will be shown, so that you can see how the
                        scores fit into the bigger picture)
                      </small>
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
              </Accordion>
            </div>
            <div className="modal-footer d-flex justify-content-between align-items-center border-0">
              <button
                type="button"
                className="btn btn_outline_danger px-5"
                onClick={handleClearFilters}
                data-dismiss="modal"
              >
                CLEAR
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-5"
                onClick={handleSearch}
              >
                SEARCH
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Download Preview Overlay */}
      {showDownloadPreview && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            ref={downloadRef}
            style={{
              width: "1000px",
              backgroundColor: "#FFFFFF",
              borderRadius: "8px 8px 0 0",
              padding: "24px",
              display: "flex",
              gap: "20px",
            }}
          >
            {/* Chart */}
            <div style={{ flex: 1, height: "400px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <span
                  style={{
                    fontWeight: "700",
                    fontSize: "18px",
                    color: "#1F2937",
                  }}
                >
                  Functional Scores Graph
                </span>
                <span style={{ fontSize: "12px", color: "#6B7280" }}>
                  Score Range: 1-5
                </span>
              </div>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart
                  data={data}
                  margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
                >
                  <CartesianGrid
                    stroke="#E5E7EB"
                    strokeDasharray="3 3"
                    vertical={false}
                    horizontal={true}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: "#6B7280" }}
                    axisLine={{ stroke: "#E5E7EB" }}
                    tickLine={{ stroke: "#E5E7EB" }}
                    tickMargin={10}
                    label={{
                      value: "Beginning Week of (Sunday)",
                      position: "insideBottom",
                      offset: -15,
                      style: {
                        textAnchor: "middle",
                        fontSize: "13px",
                        fill: "#6B7280",
                        fontWeight: 500,
                      },
                    }}
                  />
                  <YAxis
                    domain={[
                      graphScaleValues[0],
                      graphScaleValues[graphScaleValues.length - 1],
                    ]}
                    ticks={graphScaleValues}
                    tick={{ fontSize: 12, fill: "#6B7280" }}
                    axisLine={{ stroke: "#E5E7EB" }}
                    tickLine={{ stroke: "#E5E7EB" }}
                    tickMargin={10}
                    label={{
                      value: "Median Scores",
                      angle: -90,
                      position: "insideLeft",
                      style: {
                        textAnchor: "middle",
                        fontSize: "12px",
                        fill: "#6B7280",
                      },
                    }}
                  />
                  {Object.keys(visibility).map(
                    (key) =>
                      visibility[key] && (
                        <Line
                          key={key}
                          dataKey={key}
                          stroke={lineColors[key]}
                          strokeWidth={3}
                          fill="none"
                          dot={{
                            r: 4,
                            fill: lineColors[key],
                            strokeWidth: 2,
                            stroke: "#ffffff",
                          }}
                          connectNulls={true}
                          type="number"
                          isAnimationActive={false}
                        />
                      )
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Legend */}
            <div
              style={{
                width: "220px",
                borderLeft: "1px solid #E5E7EB",
                paddingLeft: "20px",
              }}
            >
              <span
                style={{
                  fontWeight: "700",
                  fontSize: "14px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "12px",
                }}
              >
                Functional Outcomes
              </span>
              {functionalIssues?.data?.[0]?.headings?.map((heading) => {
                const visibleSubs = heading.subheadings.filter(
                  (s) => visibility[s.name]
                );
                if (visibleSubs.length === 0) return null;
                return (
                  <div key={heading.name} style={{ marginBottom: "12px" }}>
                    <span
                      style={{
                        fontWeight: "700",
                        fontSize: "13px",
                        color: "#1F2937",
                        display: "block",
                        marginBottom: "6px",
                      }}
                    >
                      {heading.name}
                    </span>
                    {visibleSubs.map((sub) => (
                      <div
                        key={sub.name}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <div
                          style={{
                            width: "20px",
                            height: "4px",
                            backgroundColor:
                              lineColors[sub.name] || sub.color || "#6B7280",
                            borderRadius: "2px",
                            marginRight: "8px",
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#111827",
                            fontWeight: "600",
                          }}
                        >
                          {sub.name}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Buttons outside downloadRef so they won't be captured */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              width: "1000px",
              backgroundColor: "#FFFFFF",
              borderRadius: "0 0 8px 8px",
              padding: "16px 24px",
            }}
          >
            <button
              onClick={handleCancelDownload}
              style={{
                padding: "8px 24px",
                borderRadius: "6px",
                border: "1px solid #D1D5DB",
                backgroundColor: "#FFFFFF",
                color: "#374151",
                fontWeight: "600",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDownload}
              disabled={isDownloading}
              style={{
                padding: "8px 24px",
                borderRadius: "6px",
                border: "none",
                backgroundColor: isDownloading ? "#9CA3AF" : "#50A193",
                color: "#FFFFFF",
                fontWeight: "600",
                fontSize: "14px",
                cursor: isDownloading ? "not-allowed" : "pointer",
              }}
            >
              {isDownloading ? "Downloading..." : "Download"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FunctionalSummariesGraph;
